import { esc } from "./escape.js";

// Shared photo + emoji picker markup/wiring for the Add and Edit menu-item
// forms (pointbunny-app-todos.md, requested 2026-08-19). Both panels are
// always visible and interactive at once (no Photo/Emoji tab switch - removed
// 2026-08-20, since a photo and an emoji are set through two entirely
// different controls anyway, a mode switch was an extra click for no reason).
// `image_url`/`icon_emoji` are still mutually exclusive server-side though
// (see model.js uploadNewMenuItem/updateMenuItem) - whichever the owner
// touched LAST decides which one wins at submit time, tracked via the hidden
// `imageMode` field: selecting an emoji sets it to "emoji", choosing a photo
// (click-browse or drag-drop) sets it back to "photo". Both forms submit via
// FormData, so `imageMode`/`iconEmoji` flow straight into the
// controller/model without any extra plumbing.

export const EMOJI_GROUPS = [
  { id: "food", label: "Food", emojis: ["🍔", "🍕", "🌭", "🌮", "🌯", "🍗", "🍖", "🥪", "🍜", "🍲", "🍱", "🍣", "🍤", "🥗", "🍳", "🥞", "🧇", "🥓", "🧀", "🍦", "🍨", "🍩", "🍪", "🎂", "🧁", "🍫", "🍿", "🥐", "🥯", "🍎", "🍌", "🍇"] },
  { id: "drinks", label: "Drinks", emojis: ["☕", "🍵", "🧋", "🥤", "🧃", "🧉", "🍺", "🍷", "🍸", "🍹", "🥂", "🥛", "🧊"] },
  { id: "shop", label: "Shop", emojis: ["👕", "👗", "👟", "👜", "💄", "💇", "✂️", "🧴", "🧼", "🕯️", "📷", "🎧", "📱", "💻", "🎁", "🛍️", "🧸", "📚", "🖊️", "🔧", "🔨", "🚗", "🧹", "🐾", "🌸", "💐"] },
  { id: "other", label: "Other", emojis: ["⭐", "✨", "💎", "🔥", "❤️", "🎉", "🏆", "🎯", "📦", "✅", "🍀", "🎈"] },
];

const findGroup = (emoji) =>
  EMOJI_GROUPS.find((g) => g.emojis.includes(emoji))?.id ?? EMOJI_GROUPS[0].id;

// The emoji picker panel: live preview, a free-typed/pasted input, and a
// curated category-tabbed grid so it's still fast to scan on a POS screen.
export const emojiPanelMarkup = function ({ selectedEmoji, extraClass = "" } = {}) {
  const activeCat = findGroup(selectedEmoji);
  return `
    <div class="item-emoji-panel${extraClass ? ` ${extraClass}` : ""}">
      <div class="item-emoji-top">
        <div class="item-emoji-preview">${selectedEmoji ? esc(selectedEmoji) : "🙂"}</div>
        <label class="item-emoji-custom-label">
          Or paste any emoji
          <input type="text" class="item-emoji-custom-input" maxlength="8" placeholder="🙂" value="${esc(EMOJI_GROUPS.some((g) => g.emojis.includes(selectedEmoji)) ? "" : selectedEmoji || "")}" />
        </label>
      </div>
      <div class="item-emoji-cats" role="tablist">
        ${EMOJI_GROUPS.map((g) => `<button type="button" class="item-emoji-cat${g.id === activeCat ? " item-emoji-cat--active" : ""}" data-emoji-cat="${g.id}">${esc(g.label)}</button>`).join("")}
      </div>
      ${EMOJI_GROUPS.map((g) => `
        <div class="item-emoji-grid${g.id === activeCat ? "" : " hidden"}" data-emoji-grid="${g.id}">
          ${g.emojis.map((e) => `<button type="button" class="item-emoji-opt${e === selectedEmoji ? " item-emoji-opt--selected" : ""}" data-emoji="${e}" aria-label="${e}">${e}</button>`).join("")}
        </div>`).join("")}
    </div>`;
};

// The two form fields the controller/model actually read.
export const visualHiddenInputsMarkup = function ({ mode, selectedEmoji }) {
  return `
    <input type="hidden" name="imageMode" class="item-image-mode-input" value="${mode}" />
    <input type="hidden" name="iconEmoji" value="${esc(selectedEmoji || "")}" />`;
};

// One delegated listener set, attached once to a persistent container
// (the modal/form parent), covers every re-render of the form inside it.
export const wireVisualPicker = function (root) {
  root.addEventListener("click", (e) => {
    const catBtn = e.target.closest(".item-emoji-cat");
    if (catBtn) {
      const panel = catBtn.closest(".item-emoji-panel");
      panel.querySelectorAll(".item-emoji-cat").forEach((b) => b.classList.toggle("item-emoji-cat--active", b === catBtn));
      panel.querySelectorAll("[data-emoji-grid]").forEach((g) => g.classList.toggle("hidden", g.dataset.emojiGrid !== catBtn.dataset.emojiCat));
      return;
    }

    const opt = e.target.closest(".item-emoji-opt");
    if (opt) {
      selectEmoji(opt.closest(".item-emoji-panel"), opt.dataset.emoji);
      return;
    }
  });

  root.addEventListener("input", (e) => {
    const input = e.target.closest(".item-emoji-custom-input");
    if (!input) return;
    selectEmoji(input.closest(".item-emoji-panel"), input.value.trim(), { skipCustomInput: true });
  });

  // A successfully chosen photo makes Photo the mode that saves - the mirror
  // of selectEmoji flipping it to "emoji" below. Covers native file-picker
  // selection directly; the Edit form's drag-and-drop handler
  // (menuEditView.js _addPhotoDropZone) dispatches this same change event
  // after assigning `input.files`, so it's covered too.
  root.addEventListener("change", (e) => {
    const input = e.target.closest('input[type="file"][name="image"]');
    if (!input || !input.files?.[0]) return;
    const modeInput = input.closest("form")?.querySelector('input[name="imageMode"]');
    if (modeInput) modeInput.value = "photo";
  });

  // Enter in the custom field should confirm the pick, not submit the item form.
  root.addEventListener("keydown", (e) => {
    if (e.key !== "Enter") return;
    if (!e.target.closest(".item-emoji-custom-input")) return;
    e.preventDefault();
    e.target.blur();
  });
};

const selectEmoji = function (panel, emoji, { skipCustomInput = false } = {}) {
  if (!panel) return;
  const form = panel.closest("form");
  const emojiInput = form?.querySelector('input[name="iconEmoji"]');
  if (emojiInput) emojiInput.value = emoji;
  if (emoji) {
    const modeInput = form?.querySelector('input[name="imageMode"]');
    if (modeInput) modeInput.value = "emoji";
  }
  const preview = panel.querySelector(".item-emoji-preview");
  if (preview) preview.textContent = emoji || "🙂";
  panel.querySelectorAll(".item-emoji-opt").forEach((b) => b.classList.toggle("item-emoji-opt--selected", b.dataset.emoji === emoji));
  if (!skipCustomInput) {
    const customInput = panel.querySelector(".item-emoji-custom-input");
    if (customInput) customInput.value = "";
  }
};

// Used by the Add form's reset-on-close: clears any staged emoji and snaps
// the save-mode back to Photo, so a reopened form never carries over a
// discarded pick.
export const resetVisualPicker = function (root) {
  const panel = root.querySelector(".item-emoji-panel");
  if (panel) selectEmoji(panel, "");
  const modeInput = root.querySelector('input[name="imageMode"]');
  if (modeInput) modeInput.value = "photo";
};
